import {
  CourtRoom,
  CourtSession,
  PaymentRecord,
  MemberBalance,
  Settlement,
  Currency,
  CURRENCY_SYMBOLS,
} from '../types';

/**
 * BalanceCalculator — Pure calculation logic for court session balances.
 * No side effects, no network calls. Operates on in-memory data.
 */
export class BalanceCalculator {
  /**
   * Calculate balances for all members in a room.
   * Takes into account sessions (who paid, who attended) and settlement payments.
   */
  static calculateBalances(room: CourtRoom): MemberBalance[] {
    const balanceMap = new Map<string, MemberBalance>();

    // Initialize balances for all members
    for (const member of room.members) {
      balanceMap.set(member.id, {
        memberId: member.id,
        memberName: member.name,
        totalPaid: 0,
        totalOwed: 0,
        totalSettled: 0,
        netBalance: 0,
      });
    }

    // Calculate from sessions
    for (const session of room.sessions) {
      const attendeeCount = session.attendees.length;
      if (attendeeCount === 0) continue;

      const sharePerPerson = session.totalCost / attendeeCount;

      // The payer paid the total cost
      const payerBalance = balanceMap.get(session.paidBy);
      if (payerBalance) {
        payerBalance.totalPaid += session.totalCost;
      }

      // Each attendee owes their share
      for (const attendeeId of session.attendees) {
        const attendeeBalance = balanceMap.get(attendeeId);
        if (attendeeBalance) {
          attendeeBalance.totalOwed += sharePerPerson;
        }
      }
    }

    // Apply settlement payments
    const payments = room.payments || [];
    for (const payment of payments) {
      // fromMember paid toMember — reduces fromMember's debt
      const fromBalance = balanceMap.get(payment.fromMemberId);
      if (fromBalance) {
        fromBalance.totalSettled += payment.amount;
      }
      // toMember received — reduces their credit
      const toBalance = balanceMap.get(payment.toMemberId);
      if (toBalance) {
        toBalance.totalSettled -= payment.amount;
      }
    }

    // Calculate net balance:
    // netBalance = (totalPaid - totalOwed) + totalSettled
    // positive = others owe them, negative = they owe others
    for (const balance of balanceMap.values()) {
      balance.netBalance = (balance.totalPaid - balance.totalOwed) + balance.totalSettled;
    }

    return Array.from(balanceMap.values());
  }

  /**
   * Calculate optimal settlements using greedy algorithm.
   * Matches biggest creditor with biggest debtor to minimize transactions.
   */
  static calculateSettlements(room: CourtRoom): Settlement[] {
    const balances = this.calculateBalances(room);
    const settlements: Settlement[] = [];
    const THRESHOLD = 0.01; // Floating point threshold

    // Separate into creditors (positive balance) and debtors (negative balance)
    const creditors = balances
      .filter(b => b.netBalance > THRESHOLD)
      .map(b => ({ ...b }));
    const debtors = balances
      .filter(b => b.netBalance < -THRESHOLD)
      .map(b => ({ ...b }));

    // Sort: biggest creditor first, biggest debtor first (most negative)
    creditors.sort((a, b) => b.netBalance - a.netBalance);
    debtors.sort((a, b) => a.netBalance - b.netBalance);

    let i = 0;
    let j = 0;

    while (i < creditors.length && j < debtors.length) {
      const creditor = creditors[i];
      const debtor = debtors[j];

      const amount = Math.min(creditor.netBalance, Math.abs(debtor.netBalance));

      if (amount > THRESHOLD) {
        settlements.push({
          fromMemberId: debtor.memberId,
          fromMemberName: debtor.memberName,
          toMemberId: creditor.memberId,
          toMemberName: creditor.memberName,
          amount: Math.round(amount * 100) / 100,
        });
      }

      creditor.netBalance -= amount;
      debtor.netBalance += amount;

      if (creditor.netBalance < THRESHOLD) i++;
      if (debtor.netBalance > -THRESHOLD) j++;
    }

    return settlements;
  }

  /**
   * Get summary statistics for a room.
   */
  static getSummaryStats(room: CourtRoom): {
    totalSessions: number;
    totalSpent: number;
    averagePerSession: number;
    averagePerPerson: number;
  } {
    const totalSessions = room.sessions.length;
    const totalSpent = room.sessions.reduce((sum, s) => sum + s.totalCost, 0);
    const averagePerSession = totalSessions > 0 ? totalSpent / totalSessions : 0;

    // Average per person across all sessions
    let totalPersonSessions = 0;
    for (const session of room.sessions) {
      totalPersonSessions += session.attendees.length;
    }
    const averagePerPerson = totalPersonSessions > 0 ? totalSpent / totalPersonSessions : 0;

    return {
      totalSessions,
      totalSpent: Math.round(totalSpent * 100) / 100,
      averagePerSession: Math.round(averagePerSession * 100) / 100,
      averagePerPerson: Math.round(averagePerPerson * 100) / 100,
    };
  }

  /**
   * Count sessions paid by each member.
   */
  static getSessionsPaidCount(sessions: CourtSession[]): Map<string, number> {
    const counts = new Map<string, number>();
    for (const session of sessions) {
      counts.set(session.paidBy, (counts.get(session.paidBy) || 0) + 1);
    }
    return counts;
  }

  /**
   * Format a monetary amount with the room's currency symbol.
   */
  static formatAmount(amount: number, currency: Currency): string {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    return `${symbol}${Math.abs(amount).toFixed(2)}`;
  }

  /**
   * Format a signed balance amount (e.g., "+$15.00" or "-$8.50").
   */
  static formatBalance(amount: number, currency: Currency): string {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    const sign = amount >= 0 ? '+' : '-';
    return `${sign}${symbol}${Math.abs(amount).toFixed(2)}`;
  }
}
